/**
 * @author Kajabi, LLC <info@kajabi.com>
 * @license MIT
 */
import { useEffect, useCallback } from "react";
import { useApi } from "@backstage/core";
import { useAsyncFn } from "react-use";
import { useEntity } from "@backstage/plugin-catalog-react";
import { handleError } from "./utils";
import {
  honeybadgerApiRef,
  SORT_OPTIONS,
} from "@internal/plugin-honeybadger-api";
import { useProjectEntity } from "./useProjectEntity";

/**
 * Retrieve faults (errors) from the Honeybadger API
 */
export const useFaults = ({
  sortBy,
  limit,
}: {
  sortBy?: SORT_OPTIONS;
  limit?: string;
}) => {
  const api = useApi(honeybadgerApiRef);

  const entity = useEntity();
  const { projectKeys, queries } = useProjectEntity(entity?.entity);

  const getFaults = useCallback(async () => {
    try {
      const q = await Promise.all(
        projectKeys.map(async (projectKey: string) => {
          return await api.getFaults({
            projectKey,
            query: queries[projectKey],
            sortBy,
            limit,
          });
        })
      );

      const faults = await Promise.all(
        q.map(async (r: any) => {
          const res = r.results;
          return Promise.all(
            res.map(async (rf: any) => {
              const name = (
                await api.getProjectByKey({ projectKey: rf.project_id })
              ).name;
              return {
                ...rf,
                project_name: name,
              };
            })
          );
        })
      );

      return faults.reduce((acc: any, curr: any) => acc.concat(curr), []);
    } catch (err: any) {
      return handleError(err);
    }
  }, [api, projectKeys, queries, sortBy, limit]);

  const [state, fetchFaults] = useAsyncFn(() => getFaults(), []);

  useEffect(() => {
    fetchFaults();
  }, [fetchFaults]);

  return {
    loading: state.loading,
    error: state.error,
    results: state?.value,
    fetchFaults,
  };
};
